import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Card,
  ConfigProvider,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined, EditOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons';
import {
  associationIds,
  buildGanaPatch,
  createGana,
  deleteGana,
  getGana,
  listGana,
  updateGana,
  type Gana,
  type GanaAssociation,
} from '../api/ganaApi';
import { searchSubantas, type Subanta } from '../api/subantaApi';
import type { GanaSubantaSummary } from '../api/ganaApi';
import './GanaPage.css';

type GanaFormValues = {
  name: string;
  associations: number[];
};

type SubantaOption = { value: number; label: string; subanta?: GanaSubantaSummary | Subanta | null };

function errMessage(err: unknown, fallback: string): string {
  const ax = err as { response?: { data?: { message?: string | string[] } }; message?: string };
  const msg = ax?.response?.data?.message;
  if (Array.isArray(msg)) return msg.join('; ');
  if (typeof msg === 'string') return msg;
  return ax?.message || fallback;
}

function subantaOptionLabel(s: { id: number; word: string; iast?: string; linga?: string }): string {
  const parts = [s.word];
  const iast = s.iast?.trim();
  const linga = s.linga?.trim();
  if (iast) parts.push(iast);
  if (linga) parts.push(linga);
  return `${parts.join(' — ')} (#${s.id})`;
}

function associationsToOptions(associations: GanaAssociation[]): SubantaOption[] {
  return associations.map((a) => {
    const s = a.subanta;
    if (s) return { value: a.subantaId, label: subantaOptionLabel(s), subanta: s };
    return { value: a.subantaId, label: `#${a.subantaId}`, subanta: null };
  });
}

function mergeSubantaOptions(base: SubantaOption[], extra: SubantaOption[]): SubantaOption[] {
  const byId = new Map<number, SubantaOption>();
  for (const o of [...base, ...extra]) {
    if (!byId.has(o.value)) byId.set(o.value, o);
  }
  return Array.from(byId.values()).sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
  );
}

function subantaRowsToOptions(rows: Subanta[]): SubantaOption[] {
  return rows.map((s) => ({
    value: s.id,
    label: subantaOptionLabel(s),
    subanta: s,
  }));
}

export function GanaPage() {
  const [rows, setRows] = useState<Gana[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Gana | null>(null);
  const [saving, setSaving] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState<Gana | null>(null);
  const [form] = Form.useForm<GanaFormValues>();

  const [subantaOptions, setSubantaOptions] = useState<SubantaOption[]>([]);
  const [subantaSearchLoading, setSubantaSearchLoading] = useState(false);
  const searchSeq = useRef(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await listGana());
    } catch (err: unknown) {
      message.error(errMessage(err, 'Failed to load gana'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const runSubantaSearch = useCallback(async (query: string, seed: SubantaOption[] = []) => {
    const word = query.trim();
    if (!word) {
      setSubantaOptions(seed);
      return;
    }
    const seq = ++searchSeq.current;
    setSubantaSearchLoading(true);
    try {
      const found = await searchSubantas({ word, mode: 'exact', limit: 300 });
      if (seq !== searchSeq.current) return;
      setSubantaOptions(mergeSubantaOptions(subantaRowsToOptions(found), seed));
    } catch (err: unknown) {
      if (seq !== searchSeq.current) return;
      message.error(errMessage(err, 'Subanta search failed'));
    } finally {
      if (seq === searchSeq.current) setSubantaSearchLoading(false);
    }
  }, []);

  const openCreate = () => {
    setEditing(null);
    setSubantaOptions([]);
    form.resetFields();
    form.setFieldsValue({ name: '', associations: [] });
    setModalOpen(true);
  };

  const openEdit = async (record: Gana) => {
    try {
      const full = await getGana(record.id);
      const seed = associationsToOptions(full.associations);
      setEditing(full);
      setSubantaOptions(seed);
      form.setFieldsValue({
        name: full.name,
        associations: associationIds(full.associations),
      });
      setModalOpen(true);
    } catch (err: unknown) {
      message.error(errMessage(err, 'Failed to load gana'));
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    form.resetFields();
    setSubantaOptions([]);
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      const name = values.name.trim();
      if (!name) {
        message.error('Name is required');
        return;
      }
      const associations = (values.associations ?? []).map(Number).filter((n) => Number.isFinite(n));
      setSaving(true);
      if (editing) {
        const patch = buildGanaPatch(editing, { name, associations });
        if (Object.keys(patch).length === 0) {
          message.info('No changes to save');
          closeModal();
          return;
        }
        await updateGana(editing.id, patch);
        message.success('Gana updated');
      } else {
        await createGana({ name, associations });
        message.success('Gana created');
      }
      closeModal();
      await load();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      message.error(errMessage(err, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const openDetail = async (id: number) => {
    setDetailLoading(true);
    setDetailOpen(true);
    setDetail(null);
    try {
      setDetail(await getGana(id));
    } catch (err: unknown) {
      message.error(errMessage(err, 'Failed to load gana'));
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteGana(id);
      message.success('Deleted');
      await load();
    } catch (err: unknown) {
      message.error(errMessage(err, 'Delete failed'));
    }
  };

  const columns: ColumnsType<Gana> = [
    { title: 'ID', dataIndex: 'id', width: 72 },
    { title: 'Name', dataIndex: 'name', ellipsis: true },
    {
      title: 'Subantas',
      key: 'associations',
      width: 100,
      render: (_, r) => r.associations.length,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => void openDetail(record.id)}>
            View
          </Button>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => void openEdit(record)}>
            Edit
          </Button>
          <Popconfirm title="Delete this gana?" onConfirm={() => void handleDelete(record.id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const associationLabel = (a: GanaAssociation) => {
    if (a.subanta) return subantaOptionLabel(a.subanta);
    const opt = subantaOptions.find((o) => o.value === a.subantaId);
    return opt?.label ?? `#${a.subantaId}`;
  };

  return (
    <ConfigProvider theme={{ token: { fontFamily: "'Knswami', sans-serif" } }}>
      <div className="gana-page-root" style={{ width: '100%' }}>
        <Card
          title="Gana"
          extra={
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Add gana
            </Button>
          }
        >
          <Table<Gana>
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={rows}
            pagination={{ pageSize: 20, showSizeChanger: true }}
            scroll={{ x: 640 }}
          />

          <Modal
            title={editing ? 'Edit gana' : 'Add gana'}
            open={modalOpen}
            onCancel={closeModal}
            onOk={() => void onSubmit()}
            confirmLoading={saving}
            width={640}
            destroyOnClose
          >
            <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
              Search subantas (exact match, up to 300). Sending associations replaces all links on update.
            </Typography.Paragraph>
            <Form layout="vertical" form={form} preserve={false}>
              <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name is required' }]}>
                <Input placeholder="e.g. गण-१" />
              </Form.Item>
              <Form.Item name="associations" label="Subantas">
                <Select
                  mode="multiple"
                  showSearch
                  allowClear
                  filterOption={false}
                  placeholder="Type a word to search subantas…"
                  options={subantaOptions}
                  loading={subantaSearchLoading}
                  onSearch={(q) => {
                    const seed = associationsToOptions(
                      (editing?.associations ?? []).filter((a) =>
                        (form.getFieldValue('associations') as number[] | undefined)?.includes(a.subantaId)
                      )
                    );
                    void runSubantaSearch(q, seed);
                  }}
                  onOpenChange={(open) => {
                    if (!open) return;
                    const selected = (form.getFieldValue('associations') as number[] | undefined) ?? [];
                    const seed = mergeSubantaOptions(
                      subantaOptions,
                      associationsToOptions(
                        (editing?.associations ?? []).filter((a) => selected.includes(a.subantaId))
                      )
                    );
                    setSubantaOptions(seed);
                  }}
                  style={{ width: '100%' }}
                  popupMatchSelectWidth={false}
                  styles={{ popup: { root: { minWidth: 400 } } }}
                />
              </Form.Item>
            </Form>
          </Modal>

          <Modal
            title={detail ? detail.name : 'Gana'}
            open={detailOpen}
            onCancel={() => {
              setDetailOpen(false);
              setDetail(null);
            }}
            footer={null}
            width={560}
          >
            {detailLoading ? (
              <Typography.Text type="secondary">Loading…</Typography.Text>
            ) : detail ? (
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <div>
                  <Typography.Text type="secondary">ID</Typography.Text>
                  <div>{detail.id}</div>
                </div>
                <div>
                  <Typography.Text type="secondary">Name</Typography.Text>
                  <div>{detail.name}</div>
                </div>
                <div>
                  <Typography.Text type="secondary">Associated subantas</Typography.Text>
                  {detail.associations.length > 0 ? (
                    <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                      {detail.associations.map((a) => (
                        <li key={a.subantaId} style={{ marginBottom: 6 }}>
                          {associationLabel(a)}
                          {a.subanta?.linga ? (
                            <Tag style={{ marginLeft: 8 }}>{a.subanta.linga}</Tag>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <Typography.Text type="secondary">None</Typography.Text>
                  )}
                </div>
              </Space>
            ) : null}
          </Modal>
        </Card>
      </div>
    </ConfigProvider>
  );
}
